/**
 * ContractsProvider — site context for the Budgets and Contracts module
 * (plan §4.1 file layout, §4.9: every contracts request is site-scoped and
 * carries X-Site-Id). Mounted once in App.tsx's ModuleProviders so it
 * persists across cross-module navigation, the same pattern as every other
 * module provider (InsightsProvider, EregProvider, StudyOrganizerProvider, …).
 *
 * WP-03 scope only: exposes the current site (and super_admin) so tabs and
 * shell components can read it without each one importing useAuth directly.
 * A fetch/rollup cache like InsightsContext's belongs to whichever later WP
 * first needs one (rate card, workspace) — this provider does not grow that
 * layer speculatively (G2: no code with no requirement behind it).
 */
import { createContext, useContext, ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface ContractsContextValue {
  /** Active site id, or null when none is selected (e.g. a super_admin with
   * no sites). Every /api/contracts request needs this as X-Site-Id. */
  siteId: string | null;
  siteName: string | null;
  networkId: string | null;
  isSuperAdmin: boolean;
}

const ContractsContext = createContext<ContractsContextValue | undefined>(undefined);

export function ContractsProvider({ children }: { children: ReactNode }) {
  const { currentSite, isSuperAdmin } = useAuth();

  const value: ContractsContextValue = {
    siteId: currentSite?.id ?? null,
    siteName: currentSite?.name ?? null,
    networkId: currentSite?.network_id ?? null,
    isSuperAdmin,
  };

  return <ContractsContext.Provider value={value}>{children}</ContractsContext.Provider>;
}

export function useContracts(): ContractsContextValue {
  const ctx = useContext(ContractsContext);
  if (!ctx) throw new Error('useContracts must be used within ContractsProvider');
  return ctx;
}

export default ContractsProvider;
